import { useState, useRef, useEffect } from "react";
import { api } from "../lib/api";
import { MessageCircle, X, Send } from "lucide-react";

const GREETING = "Hi, I'm Daisy. I can answer questions about extensions, our process, planning, pricing and more — and if you'd like, I can pass your details to the team. What can I help with?";

export function ChatWidget({ hideLauncherOnMobile = false }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ id: "greeting", role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(localStorage.getItem("de_chat_session") || "");
  const contextRef = useRef({});
  const endRef = useRef(null);

  useEffect(() => { if (open) endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  useEffect(() => {
    // Other components (e.g. the Quote page's postcode step) can auto-open Daisy
    // with a tailored greeting and stash session context (like the failed postcode)
    // that gets forwarded to the backend on every subsequent /chat call.
    const onOpen = (ev) => {
      const detail = ev.detail || {};
      setOpen(true);
      if (detail.postcode || detail.source) {
        contextRef.current = {
          ...contextRef.current,
          ...(detail.postcode ? { postcode: detail.postcode } : {}),
          ...(detail.source ? { source: detail.source } : {}),
        };
      }
      if (detail.greeting) {
        setMessages(m => {
          if (m.some(msg => msg.content === detail.greeting)) return m;
          return [...m, { id: crypto.randomUUID(), role: "assistant", content: detail.greeting }];
        });
      }
    };
    window.addEventListener("daisy:open", onOpen);
    return () => window.removeEventListener("daisy:open", onOpen);
  }, []);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setMessages(m => [...m, { id: crypto.randomUUID(), role: "user", content: text }]);
    setInput("");
    setBusy(true);
    try {
      const r = await api.post("/chat", {
        session_id: sessionRef.current,
        message: text,
        context: contextRef.current,
      });
      if (r.data.session_id) { sessionRef.current = r.data.session_id; localStorage.setItem("de_chat_session", r.data.session_id); }
      setMessages(m => [...m, { id: crypto.randomUUID(), role: "assistant", content: r.data.reply }]);
    } catch (err) {
      setMessages(m => [...m, { id: crypto.randomUUID(), role: "assistant", content: "Sorry, I hit a snag. Please call 0203 488 1081 or use the quote tool." }]);
    }
    setBusy(false);
  };

  return (
    <>
      {!open && (
        <button data-testid="chat-open" onClick={() => setOpen(true)}
          className={`fixed bottom-24 right-4 sm:bottom-20 sm:right-5 z-[60] ${hideLauncherOnMobile ? "hidden sm:flex" : "flex"} items-center gap-3 bg-[#C79A3B] hover:bg-[#A87E28] text-white rounded-full p-1.5 sm:pl-5 sm:pr-2 sm:py-2 shadow-xl transition-colors`}
          aria-label="Open chat with Daisy">
          <span className="hidden sm:inline text-sm font-bold whitespace-nowrap">Need help? Ask Daisy</span>
          <span className="relative flex items-center justify-center">
            <span className="absolute inline-flex h-full w-full rounded-full bg-white/40 animate-ping" />
            <span className="relative rounded-full bg-white text-[#C79A3B] w-11 h-11 sm:w-10 sm:h-10 flex items-center justify-center"><MessageCircle size={22} /></span>
          </span>
        </button>
      )}
      {open && (
        <div data-testid="chat-panel" className="fixed inset-0 sm:inset-auto sm:bottom-20 sm:right-5 z-[60] w-full sm:w-[92vw] sm:max-w-sm h-full sm:h-[70vh] sm:max-h-[560px] bg-white sm:border border-[#E5E7EB] shadow-2xl flex flex-col">
          <div className="flex items-center justify-between bg-[#12263A] text-white px-4 py-3">
            <div>
              <div className="font-bold text-sm">Chat with Daisy</div>
              <div className="text-[11px] text-[#cdbfae]">The Extension Works assistant</div>
            </div>
            <button data-testid="chat-close" onClick={() => setOpen(false)} aria-label="Close chat" className="hover:text-[#E8A07E]"><X size={20} /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-[#F4F4F4]" data-testid="chat-messages">
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div data-testid={`chat-msg-${m.role}`} className={`max-w-[85%] px-3 py-2 text-sm rounded-lg whitespace-pre-line ${m.role === "user" ? "bg-[#C79A3B] text-white" : "bg-white border border-[#E5E7EB] text-[#12263A]"}`}>{m.content}</div>
              </div>
            ))}
            {busy && <div className="flex justify-start"><div className="bg-white border border-[#E5E7EB] px-3 py-2 text-sm text-[#6B7280] rounded-lg">Daisy is typing…</div></div>}
            <div ref={endRef} />
          </div>
          <div className="border-t border-[#E5E7EB] p-2 flex gap-2">
            <input data-testid="chat-input" value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") send(); }}
              placeholder="Ask a question…" className="flex-1 border border-[#E5E7EB] px-3 py-2 text-sm focus:outline-none focus:border-[#C79A3B]" />
            <button data-testid="chat-send" onClick={send} disabled={busy || !input.trim()} className="bg-[#C79A3B] hover:bg-[#A87E28] disabled:opacity-50 text-white px-3 flex items-center justify-center transition-colors"><Send size={16} /></button>
          </div>
        </div>
      )}
    </>
  );
}
